import clsx from 'clsx';
import { isEmpty, pickBy } from 'lodash-es';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { withErrorBoundary } from 'src/components/common/LocalErrorBoundary';
import { EventVenueMap } from 'src/components/Events/VenueMap/EventVenueMap';
import { useVenueMapHandlers } from 'src/components/Listings/MapWithCompListingsPreview/MapWithCompListingsPreview.hooks';
import { Content } from 'src/contexts/ContentContext';
import { useInventoryEventDetailContext } from 'src/contexts/InventoryEventDetailContext/InventoryEventDetailContext';
import { Stack } from 'src/core/ui';
import {
  ListingCompsNumberRange,
  ListingCompsSpectrum,
} from 'src/hooks/useComparableListingsVenueMapColor';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { getMatchingSectionRow } from 'src/utils/seatScoreUtils';
import { mapToSelectedSectionSettings } from 'src/utils/types/selectedSectionSettingsWithOptionals';
import { onSectionSelctionWithMyRowDefaults } from 'src/utils/venueUtils';
import {
  AutoPricingInputs,
  Feature,
  SectionInfo,
  SelectedSectionSettings,
} from 'src/WebApiController';

import { SidePanelTab } from '../../../MarketListings/MarketListingHeader';
import { getMergedSelectedSectionSettings } from '../../InventoryEventDetail.hooks';
import { CondensedVenueMapQtyFilter } from './CondensedVenueMapQtyFilter';
import * as styles from './MapBody.css';
import { SectionScoreRecommendations } from './SectionScoreRecommendations';

const EventVenueMapWithErrorBoundary = memo(withErrorBoundary(EventVenueMap));

type MapBodayProps = {
  useVerticalView: boolean;
};

export const MapBody: React.FC<MapBodayProps> = ({ useVerticalView }) => {
  const {
    selectedListing,
    selectedGroup,
    venueMapInfo,
    priceCalcQuery,
    filterSections,
    compListingSectionIds,
    shouldRepaint,
    shouldReset,
    selectedTab,
    isRecommendationDeclined,
    sectionRowScoreRecommendations,
    recommendingSectionIds,
    onToggleRecommendingSectionIds,
    onDeclineRecommendation,
    getColor,
  } = useInventoryEventDetailContext();

  const hasSelectedSectionScoreRecommendationsFeature = useUserHasFeature(
    Feature.SelectedSectionScoreRecommendations
  );

  const hasDefaultToMyRowOnSectionSelectionFeature = useUserHasFeature(
    Feature.DefaultToMyRowOnSectionSelection
  );

  const hasAutoPricingEmptySelectedSectionsNoCompsFeature = useUserHasFeature(
    Feature.AutoPricingEmptySelectedSectionsNoComps
  );

  const { setValue, watch } = useFormContext<AutoPricingInputs>();

  const { compListingSelectedSectionSettings } = watch();

  const onSelectedSectionsChange = useCallback(
    (selectedSections: SectionInfo[], newSectionIds?: number[]) => {
      if (selectedTab !== SidePanelTab.LISTINGS) {
        return;
      }

      let compListingSelectedSectionSettingsNew: SelectedSectionSettings | null =
        null;
      if (
        (selectedListing || selectedGroup) &&
        hasDefaultToMyRowOnSectionSelectionFeature
      ) {
        compListingSelectedSectionSettingsNew =
          onSectionSelctionWithMyRowDefaults(
            compListingSelectedSectionSettings,
            selectedSections,
            venueMapInfo?.sections ?? [],
            selectedListing
          );
      } else {
        const sectionIdFilter = selectedSections.map((s) => s.id);
        const selectedRowIds = selectedSections
          .flatMap(({ rows }) => rows)
          .map(({ id }) => id);
        const rowIdFilter = [];
        if (isEmpty(compListingSelectedSectionSettings?.rowIdFilter)) {
          rowIdFilter.push(...selectedRowIds);
        } else {
          rowIdFilter.push(
            ...(compListingSelectedSectionSettings?.rowIdFilter?.filter(
              (rowId) => selectedRowIds.includes(rowId)
            ) ?? [])
          );
        }
        const sectionRowIdFilter = pickBy(
          Object.assign(
            {},
            compListingSelectedSectionSettings?.sectionRowIdFilter ?? {}
          ),
          (_, key) => sectionIdFilter.includes(parseInt(key))
        );
        compListingSelectedSectionSettingsNew = {
          ...compListingSelectedSectionSettings,
          sectionIdFilter,
          rowIdFilter,
          sectionRowIdFilter,
        } as SelectedSectionSettings;
      }

      const sanitizedSelectedSectionSettings =
        mapToSelectedSectionSettings(compListingSelectedSectionSettingsNew) ??
        null;
      setValue(
        'compListingSelectedSectionSettings',
        isRecommendationDeclined
          ? sanitizedSelectedSectionSettings
          : (getMergedSelectedSectionSettings(
              sanitizedSelectedSectionSettings,
              sectionRowScoreRecommendations?.recommendedSelectedSectionSettings,
              true
            ) ?? null)
      );
      // new sections, remove from recommendations
      newSectionIds && onToggleRecommendingSectionIds(newSectionIds);
    },
    [
      compListingSelectedSectionSettings,
      hasDefaultToMyRowOnSectionSelectionFeature,
      isRecommendationDeclined,
      onToggleRecommendingSectionIds,
      sectionRowScoreRecommendations?.recommendedSelectedSectionSettings,
      selectedGroup,
      selectedListing,
      selectedTab,
      setValue,
      venueMapInfo?.sections,
    ]
  );

  useEffect(() => {
    if (
      selectedListing?.id == null &&
      selectedGroup?.ltGrpId == null &&
      shouldReset
    ) {
      const compListingSelectedSectionSettingsNew: SelectedSectionSettings = {
        sectionIdFilterFromZoneMap: false,
        includeBetterZones: false,
        rowOffset: 0,
        sectionIdFilter: [],
        rowIdFilter: [],
        sectionRowIdFilter: {},
        sectionFilterMode: null,
      };

      setValue(
        'compListingSelectedSectionSettings',
        mapToSelectedSectionSettings(compListingSelectedSectionSettingsNew) ??
          null
      );
    }
  }, [
    onSelectedSectionsChange,
    selectedGroup?.ltGrpId,
    selectedListing?.id,
    setValue,
    shouldReset,
  ]);

  const { onSectionClicked, onToggleMirrors, onSectionHovered } =
    useVenueMapHandlers(
      filterSections,
      priceCalcQuery?.data ?? undefined,
      venueMapInfo,
      onSelectedSectionsChange,
      onToggleRecommendingSectionIds
    );

  const selectedSectionIds = useMemo(() => {
    if (!selectedListing?.seating) {
      return [];
    }

    const { section } = getMatchingSectionRow(
      selectedListing.seating,
      venueMapInfo?.sections
    );
    return section ? [section.id] : [];
  }, [selectedListing?.seating, venueMapInfo?.sections]);

  const handleSelectAllSections = useCallback(() => {
    if (!venueMapInfo?.sections || selectedTab !== SidePanelTab.LISTINGS) {
      return;
    }

    if (filterSections.length > 0) {
      onSelectedSectionsChange([], []);
    } else {
      onSelectedSectionsChange(
        venueMapInfo.sections,
        venueMapInfo.sections.map((s) => s.id)
      );
    }
  }, [
    venueMapInfo?.sections,
    selectedTab,
    onSelectedSectionsChange,
    filterSections,
  ]);

  return (
    <div className={styles.mapWrapper}>
      <EventVenueMapWithErrorBoundary
        isCondensedView
        selectedSectionIds={selectedSectionIds}
        markedSectionIds={compListingSectionIds}
        animatedSectionIds={
          isRecommendationDeclined ? undefined : recommendingSectionIds
        }
        onSectionClicked={onSectionClicked}
        onSectionHovered={onSectionHovered}
        onToggleMirrors={onToggleMirrors}
        setSelectedSections={onSelectedSectionsChange}
        colorBandProps={
          !selectedListing?.id
            ? {
                numOfSteps: ListingCompsNumberRange,
                spectrum: ListingCompsSpectrum,
                labels: ['$', '$$$'],
              }
            : undefined
        }
        getColor={getColor}
        cornerContent={
          <Stack direction="row" gap="s">
            {hasSelectedSectionScoreRecommendationsFeature &&
              !isRecommendationDeclined &&
              Boolean(recommendingSectionIds?.length) && (
                <SectionScoreRecommendations
                  recommendingSectionIds={recommendingSectionIds}
                  onDeclineRecommendation={onDeclineRecommendation}
                />
              )}
            {hasAutoPricingEmptySelectedSectionsNoCompsFeature &&
              selectedTab === SidePanelTab.LISTINGS &&
              (selectedListing || selectedGroup) && (
                <button
                  onClick={handleSelectAllSections}
                  className={styles.selectAllButton}
                >
                  {filterSections.length > 0 ? (
                    <Content id={ContentId.ClearAll} />
                  ) : (
                    <Content id={ContentId.SelectAll} />
                  )}
                </button>
              )}
          </Stack>
        }
        statsContent={
          selectedListing == null && selectedGroup == null ? (
            <CondensedVenueMapQtyFilter />
          ) : null
        }
        shouldRepaint={shouldRepaint}
        mapActionContainerClassname={clsx({
          [styles.floatMapActionContainer]: useVerticalView,
        })}
      />
    </div>
  );
};
